import { InputWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import { Button, Tab, Tabs, useTheme } from '@material-ui/core'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import AddIcon from '@material-ui/icons/Add'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import SearchIcon from '@material-ui/icons/Search'
import { useCallback, useEffect, useState } from 'react'
import React from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import ChargeWalletModal from '../../components/ChargeWalletModal'
import PageHeader from '../../components/PageHeader'
import WalletCart from '../../components/WalletCart'
import { WALLET_QUERY } from '../../graphql/wallet'
import { PAYMENT_TERM_ASSIGNMENTS_QUERY } from '../../graphql/walletPaymentTerm'
import { DateObject } from '../../helpers/date'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import walletPagesRoleManagement from '../../staticRoleManagement/walletRoleManagement'
import { Wallet, WalletSection } from '../../types/wallet'
import {
  Assignment,
  PaymentTermAssignmentRow
} from '../../types/walletPaymentTerm'
import ChargeReciept from './ChargeReceipt'
import {
  pathWithoutReceipt,
  ReceiptDetails,
  ReceiptMode,
  receiptFromLocation,
  receiptPath
} from './ChargeReceipt/receipt'
import { CreditorContent } from './creditorContent'
import useStyles from './styles/index.styles'
import { usePendingTopUp } from './usePendingTopUp'
import WalletDetailContent from './walletDetailContent'

type Props = {
  setHasSettelment: React.Dispatch<React.SetStateAction<boolean>>
}
export default function WalletDetail({ setHasSettelment }: Props) {
  const classes = useStyles()
  const params = useParams<{ walletId: string }>()
  const theme = useTheme()
  const [chargeReceiptOpen, setChargeReceiptOpen] = useState<boolean>(false)
  const [chargeReceiptMode, setChargeReceiptMode] =
    useState<ReceiptMode>('Successful')
  const [chargeReceiptDetails, setChargeReceiptDetails] = useState<
    ReceiptDetails | undefined
  >(undefined)

  const location = useLocation()
  const history = useHistory()
  useEffect(() => {
    // A payment gateway returns a top-up payer to
    // /en/adopay/wallet/123/details/<successful|failed|pending>?paymentNumber=..
    const receipt = receiptFromLocation(
      location.pathname || '',
      location.search || ''
    )
    if (receipt) {
      setChargeReceiptMode(receipt.mode)
      setChargeReceiptDetails(receipt.details)
      setChargeReceiptOpen(true)
    } else {
      setChargeReceiptOpen(false)
    }
  }, [location.pathname, location.search])
  const closeChargeReceipt = () => {
    setChargeReceiptOpen(false)
    // Replace, so going back does not reopen the receipt.
    history.replace(pathWithoutReceipt(location.pathname || ''))
  }
  const [walletSection, setWalletSection] =
    useState<WalletSection>('Wallet-Details')
  const [isChargeWalletOpen, setIsChargeWalletOpen] = useState<boolean>(false)
  const [coveringRange, setcoveringRange] = useState<DateObject>({})
  const [WalletData, setWalletData] = useState<Wallet>({
    active: false,
    canUseCredit: false,
    autoSettelmentEnabled: false,
    currency: '',
    entity: {
      entityCode: '',
      entityID: '',
      entityName: '',
      entityType: '',
      displayEntityCode: ''
    },
    isBilateral: false,
    macReservable: 0,
    maxCredit: 0,
    remainingCreditBalance: 0,
    remainingReservable: 0,
    walletBalance: 0,
    walletID: '',
    walletType: ''
  })
  const [assignment, setassignment] = useState<PaymentTermAssignmentRow[]>([])
  const [searchInputValue, setsearchInputValue] = useState<string>('')

  const {
    data: wallet,
    loading: loadingForwallet,
    refetch
  } = useQuery<{
    wallet: Wallet
  }>(WALLET_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      walletID: params.walletId
    },
    onCompleted: (res) => {
      if (!res.wallet.isBilateral && !res.wallet.autoSettelmentEnabled) {
        setHasSettelment(!res.wallet.isBilateral)
      }
      setWalletData(res.wallet)
    }
  })

  // While "payment received, crediting" is shown, watch the balance until the credit lands.
  // The address turns to /successful too, so a reload does not show "pending" again.
  const showCredited = useCallback(() => {
    setChargeReceiptMode('Successful')
    history.replace(
      `${receiptPath(location.pathname || '', 'successful')}${
        location.search || ''
      }`
    )
  }, [history, location.pathname, location.search])
  const stillProcessing = usePendingTopUp({
    active: chargeReceiptOpen && chargeReceiptMode === 'Pending',
    balance: wallet?.wallet.walletBalance,
    refetch,
    onCredited: showCredited
  })

  const { loading: loadingPaymentTermAssignment } = useQuery<{
    paymentTermAssignmentsOfAWallet: PaymentTermAssignmentRow[]
  }>(PAYMENT_TERM_ASSIGNMENTS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      walletID: params.walletId
    },
    onCompleted: (res) => {
      const assignments = res.paymentTermAssignmentsOfAWallet.flatMap(
        (row) => row.assignments
      )

      if (assignments.length === 0) {
        setcoveringRange({
          gregorianStartDate: '',
          gregorianEndDate: ''
        })
        return
      }

      const earliestFrom = assignments.reduce(
        (min, assn: Assignment) =>
          assn.assignmentTimeRange.from < min
            ? assn.assignmentTimeRange.from
            : min,
        assignments[0].assignmentTimeRange.from
      )

      const latestTo = assignments.reduce(
        (max, assn: Assignment) =>
          assn.assignmentTimeRange.to > max ? assn.assignmentTimeRange.to : max,
        assignments[0].assignmentTimeRange.to
      )

      setassignment(res.paymentTermAssignmentsOfAWallet)
      setcoveringRange({
        gregorianStartDate: earliestFrom,
        gregorianEndDate: latestTo
      })
    }
  })

  const content = (input: WalletSection) => {
    switch (input) {
      case 'Wallet-Details':
        return (
          <WalletDetailContent
            wallet={WalletData}
            {...{
              assignment,
              coveringRange,
              loadingPaymentTermAssignment,
              refetch
            }}
          />
        )
      case 'Creditors':
        return (
          <CreditorContent
            wallet={WalletData}
            setHasSettelment={setHasSettelment}
            searchInputValue={searchInputValue}
            setsearchInputValue={setsearchInputValue}
          />
        )
      default:
        return <div />
    }
  }

  const canChargeWallet = walletPagesRoleManagement
    .find((item) => item.route === '/adopay/:page')
    ?.sections?.MANUAL_SETTLEMENT.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const canAddCreditors = walletPagesRoleManagement
    .find((item) => item.route === '/adopay/:page')
    ?.sections?.ADD_CREDITOR.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  return (
    <div>
      <PageHeader title={getTranslate('Wallet Details')} />
      <div className={classes.header}>
        <WalletCart
          icon={<AccountBalanceWalletIcon className={classes.color} />}
          title={getTranslate('Wallet Cash Balance')}
          balance={wallet?.wallet.walletBalance || 0}
          currency={wallet?.wallet.currency}
          cashBalance
        />
        <WalletCart
          icon={<CreditCardIcon className={classes.color} />}
          title={getTranslate('Remaining Credit Balance')}
          balance={wallet?.wallet.remainingCreditBalance || 0}
          currency={wallet?.wallet.currency}
          cashBalance={false}
        />
        <WalletCart
          icon={<CreditCardIcon className={classes.color} />}
          title={getTranslate('Remaining Reservable')}
          balance={wallet?.wallet.remainingReservable || 0}
          currency={wallet?.wallet.currency}
          cashBalance={false}
        />
        <div className={classes.buttons}>
          {canChargeWallet && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setIsChargeWalletOpen(true)
              }}>
              {getTranslate('Charge Wallet')}
            </Button>
          )}
          {canAddCreditors && wallet?.wallet.isBilateral && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              className={classes.addCreditorbutton}
              component={LocalizedLink}
              to={`/adopay/wallet/${params.walletId}/add-creditors`}>
              {getTranslate('Add Creditor')}
            </Button>
          )}
        </div>
      </div>
      <div className={classes.tabSection}>
        <Tabs
          value={walletSection}
          indicatorColor="primary"
          textColor="primary">
          <Tab
            className={classes.tab}
            value={'Wallet-Details'}
            label={getTranslate('Wallet Details')}
            onClick={() => {
              setWalletSection('Wallet-Details' as WalletSection)
            }}
          />
          {wallet?.wallet.isBilateral && (
            <Tab
              className={classes.tab}
              value={'Creditors'}
              label={getTranslate('Creditors')}
              onClick={() => {
                setWalletSection('Creditors' as WalletSection)
              }}
            />
          )}
        </Tabs>
        {walletSection === 'Creditors' && (
          <InputWithLabel
            placeholder={getTranslate('Search by creditor entity name')}
            autoComplete="off"
            noHelperText
            startIcon={<SearchIcon />}
            inputSize="small"
            value={searchInputValue}
            onChange={(event) => {
              setsearchInputValue(event.currentTarget.value)
            }}
          />
        )}
      </div>
      {content(walletSection)}

      <ChargeReciept
        mode={chargeReceiptMode}
        open={chargeReceiptOpen}
        onClose={closeChargeReceipt}
        details={chargeReceiptDetails}
        stillProcessing={stillProcessing}
      />
      {isChargeWalletOpen && (
        <ChargeWalletModal
          wallet={WalletData}
          isOpen={isChargeWalletOpen}
          setIsOpen={setIsChargeWalletOpen}
          refetch={refetch}
        />
      )}
      <AdotelSkeleton
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
        open={loadingForwallet && loadingPaymentTermAssignment}
      />
    </div>
  )
}
